import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import GroupedCodeDetailList from './GroupedCodeDetailList'
import {
  CodeDetailsAccordionProps,
  CodeValidationStatus,
} from '../../models/Validation'

const CodeDetailsAccordion = ({
  codeGroups,
  buildingData,
}: CodeDetailsAccordionProps) => {
  return (
    <Box
      sx={{
        gap: 1,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        '& .MuiAccordion-root.Mui-expanded': {
          margin: 0,
          marginLeft: '-4px',
        },
      }}
    >
      {codeGroups.map((group, idx) => {
        const totalCodes = group.codes.flatMap((code) => code.elements).length
        const failedCodes = group.codes
          .flatMap((code) => code.elements)
          .filter((c) => c.status != CodeValidationStatus.Passed).length
        // Determine indicator color
        const indicatorColor = failedCodes > 0 ? '#FF2F5F' : '#00C961'
        // # add code to the elements bedfore flatMap
        const elements = group.codes.flatMap((code) =>
          code.elements?.map((element) => ({
            ...element,
            code,
          })),
        )
        return (
          <Box key={group.groupName} display='flex'>
            {/* Vertical color bar before accordion */}
            <Box
              sx={{
                width: 9,
                minWidth: 8,
                bgcolor: indicatorColor,
                borderRadius: 4,
                marginRight: 0,
                zIndex: 2,
                position: 'relative',
                boxShadow: '0 0 2px 0 rgba(0,0,0,0.08)',
                height: 'auto',
                minHeight: '100%',
                alignSelf: 'stretch',
                clipPath: 'inset(0 50% 0 0)', // show only left half horizontally
              }}
            />
            <Accordion
              key={group.groupName + idx}
              sx={{ background: '#F5F5F5', flex: 1, ml: '-4px', zIndex: 1 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  m: 0,
                  p: 0,
                  '& .MuiAccordionSummary-content': { margin: 0 },
                }}
              >
                <Box
                  display='flex'
                  width='100%'
                  alignItems='center'
                  justifyContent='space-between'
                >
                  <Box display='flex' alignItems='center' gap={1} px={1}>
                    <Typography
                      fontWeight={600}
                      fontSize={15}
                      color='#04002E'
                      title={group.codes
                        .map((code) => code.description)
                        .join(', ')}
                    >
                      {group.groupName}
                    </Typography>
                  </Box>
                  <Box
                    display='flex'
                    alignItems='center'
                    bgcolor='#FFFFFF'
                    borderRadius={2}
                    px={1}
                    py={0.5}
                    gap={2}
                  >
                    {/* Total count */}
                    <Box display='flex' alignItems='center' gap={0.5}>
                      <Typography
                        fontSize={12}
                        fontWeight={600}
                        color='#04002E'
                      >
                        {totalCodes}
                      </Typography>
                      <Typography
                        fontSize={12}
                        fontWeight={500}
                        color='#04002E'
                      >
                        TOTAL
                      </Typography>
                    </Box>
                    <Box display='flex' alignItems='center' gap={0.5} ml={1}>
                      <Box
                        width={4}
                        height={12}
                        borderRadius={1}
                        bgcolor='#FF2F5F'
                        mr={0.5}
                      />
                      <Typography
                        fontSize={12}
                        fontWeight={600}
                        color='#04002E'
                      >
                        {failedCodes}
                      </Typography>
                      <Typography
                        fontSize={12}
                        fontWeight={500}
                        color='#04002E'
                      >
                        FAILED
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  border: '1px solid #E0E0E0',
                  borderRadius: '6px',
                  background: '#F5F5F5',
                  m: 0,
                  p: 0,
                }}
              >
                <Box display='flex' alignItems='center' gap={2} mb={2} ml={3}>
                  {/* Passed */}
                  <Box display='flex' alignItems='center' gap={0.5}>
                    <Box
                      width={4}
                      height={12}
                      borderRadius={1}
                      bgcolor='#00C961'
                    />
                    <Typography fontSize={12} fontWeight={600} color='#04002E'>
                      {
                        group.codes
                          .flatMap((c) => c.elements)
                          .filter(
                            (c) => c.status === CodeValidationStatus.Passed,
                          ).length
                      }
                    </Typography>
                    <Typography fontSize={12} fontWeight={500} color='#04002E'>
                      PASSED
                    </Typography>
                  </Box>
                  {/* Failed */}
                  <Box display='flex' alignItems='center' gap={0.5}>
                    <Box
                      width={4}
                      height={12}
                      borderRadius={1}
                      bgcolor='#FF2F5F'
                    />
                    <Typography fontSize={12} fontWeight={600} color='#04002E'>
                      {failedCodes}
                    </Typography>
                    <Typography fontSize={12} fontWeight={500} color='#04002E'>
                      FAILED
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  fontWeight={600}
                  fontSize={10}
                  color='#04002E'
                  mt={1}
                  ml={3}
                  textAlign='left'
                >
                  All Objects in the Group
                </Typography>
                <GroupedCodeDetailList
                  codes={group.codes}
                  elements={elements}
                  buildingData={buildingData}
                />
              </AccordionDetails>
            </Accordion>
          </Box>
        )
      })}
    </Box>
  )
}

export default CodeDetailsAccordion
